import { normalizeFormDateStringToIso } from '~/utils/dateFormatter';

import { normalizePayloadText } from '../htmlEntities';
import { parseNonEmptyStrings } from '../parseHelpers';

import { createDefaultSpendPointsState } from './defaults';
import type { SpendPointsPayloadBuilderState, SpendPointsWaysToBoostTile } from './types';

const PREVIEW_REQUIRED = '[required]';

function payloadText(value: string): string {
  return normalizePayloadText(value);
}

function previewRequiredValue(value: string): string {
  const normalizedValue = payloadText(value);
  return normalizedValue ? normalizedValue : PREVIEW_REQUIRED;
}

function previewDateTime(value: string): string {
  return value.trim() ? normalizeFormDateStringToIso(value) : PREVIEW_REQUIRED;
}

function buildTile(tile: SpendPointsWaysToBoostTile): Record<string, unknown> {
  return {
    endAt: normalizeFormDateStringToIso(tile.endAt),
    ctaUrl: payloadText(tile.ctaUrl),
    imageId: payloadText(tile.imageId),
    startAt: normalizeFormDateStringToIso(tile.startAt),
    isActive: tile.isActive,
    eligibleRegions: parseNonEmptyStrings(tile.enabledRegions),
    'title-i18n': payloadText(tile.title),
    'ctaLabel-i18n': payloadText(tile.ctaLabel),
    'description-i18n': payloadText(tile.description),
  };
}

function buildPreviewTile(tile: SpendPointsWaysToBoostTile): Record<string, unknown> {
  return {
    endAt: previewDateTime(tile.endAt),
    ctaUrl: previewRequiredValue(tile.ctaUrl),
    imageId: previewRequiredValue(tile.imageId),
    startAt: previewDateTime(tile.startAt),
    isActive: tile.isActive,
    eligibleRegions: parseNonEmptyStrings(tile.enabledRegions),
    'title-i18n': previewRequiredValue(tile.title),
    'ctaLabel-i18n': previewRequiredValue(tile.ctaLabel),
    'description-i18n': previewRequiredValue(tile.description),
  };
}

export function buildSpendPointsPayload(state: SpendPointsPayloadBuilderState): Record<string, unknown> {
  return {
    waysToBoostTiles: state.waysToBoostTiles.map(buildTile),
  };
}

export function buildSpendPointsPreviewPayload(state: SpendPointsPayloadBuilderState): Record<string, unknown> {
  const tiles =
    state.waysToBoostTiles.length > 0 ? state.waysToBoostTiles : createDefaultSpendPointsState().waysToBoostTiles;

  return {
    waysToBoostTiles: tiles.map(buildPreviewTile),
  };
}

export function serializeSpendPointsPayload(state: SpendPointsPayloadBuilderState): string {
  return JSON.stringify(buildSpendPointsPayload(state), null, 2);
}

export function serializeSpendPointsPreviewPayload(state: SpendPointsPayloadBuilderState): string {
  return JSON.stringify(buildSpendPointsPreviewPayload(state), null, 2);
}

export function spendPointsPayloadDownloadFilename(): string {
  return 'spend-points-payload.txt';
}
